import { motion } from "framer-motion";
import { SectionHeader } from "@/components/RevenueEngine";

const SYSTEMS = [
  {
    n: "01",
    name: "Data Foundation",
    color: "#007AFF",
    headline: "Bad CRM data costs you pipeline. We clean it once and keep it accurate forever.",
    buckets: [
      { l: "Find", d: "Surface every stale contact, missing field, and duplicate record." },
      { l: "Fix", d: "Merge dupes, archive dead contacts, validate emails." },
      { l: "Maintain", d: "Auto-refreshes as contacts change jobs, companies grow, and data decays." },
    ],
  },
  {
    n: "02",
    name: "TAM & Sales Intelligence",
    color: "#00F0FF",
    headline: "Your ideal customer list, built once and updated forever.",
    buckets: [
      { l: "Define", d: "Turn your ICP criteria into a live, filterable account universe." },
      { l: "Enrich", d: "Size, tech, contacts, verified emails & phones." },
      { l: "Prioritize", d: "Ranked tiers your reps work top-down. Highest revenue potential first." },
    ],
  },
  {
    n: "03",
    name: "Signal System",
    color: "#FFB800",
    headline: "Real-time alerts when your target accounts are ready to buy.",
    buckets: [
      { l: "Watch", d: "Monitors every buying signal that matters across your entire target list." },
      { l: "Alert", d: "Slack notifications the moment signals fire." },
      { l: "Act", d: "Triggered outreach fires automatically. No rep action needed." },
    ],
  },
  {
    n: "04",
    name: "ABM Infrastructure",
    color: "#D946EF",
    headline: "Every dollar of ad spend, every outreach touchpoint, every sales call coordinated on the same accounts at the same time.",
    buckets: [
      { l: "Rank", d: "Tiered by fit, intent & revenue potential." },
      { l: "Track", d: "Ad engagement, visits & content activity." },
      { l: "Align", d: "One account owner per record. No more stepping on each other." },
    ],
  },
  {
    n: "05",
    name: "Inbound & PLG Orchestration",
    color: "#CCFF00",
    headline: "The moment someone shows interest, they're enriched, scored, and in a rep's queue. Under 60 seconds.",
    buckets: [
      { l: "Enrich", d: "Instant company data on every form fill." },
      { l: "Score", d: "ICP match and intent score assigned the moment they hit the form." },
      { l: "Route", d: "Right rep, under a minute." },
    ],
  },
  {
    n: "06",
    name: "Sales-Ops Automation",
    color: "#FFFFFF",
    headline: "Every deal that stalls, every follow-up that gets missed, every forecast that runs late. Handled automatically.",
    buckets: [
      { l: "Advance", d: "Pipeline stages move when conditions fire." },
      { l: "Automate", d: "Follow-up tasks spawn on their own." },
      { l: "Escalate", d: "Stalled deals surface themselves before they fall out of the quarter." },
    ],
  },
  {
    n: "07",
    name: "Network & Expansion",
    color: "#FF7B5B",
    headline: "The people who already trust you are your fastest path to more revenue. We build the system that keeps them close.",
    buckets: [
      { l: "Champions", d: "Know the moment a champion changes jobs. Warm outreach triggered before a competitor gets there." },
      { l: "Networks", d: "Warm intros surfaced automatically." },
      { l: "Expand", d: "Headcount growth, usage spikes, new stakeholders. All routed to CS automatically." },
    ],
  },
  {
    n: "08",
    name: "Rep Intelligence & AI",
    color: "#5BB4FF",
    headline: "Your reps stop researching and start selling. AI does the prep work.",
    buckets: [
      { l: "Brief", d: "Company context, stakeholders, recent news, and talking points waiting in their inbox before the call." },
      { l: "Draft", d: "First-touch written from their signals. Rep reviews, edits if needed, sends." },
      { l: "Sync", d: "Call ends, CRM updates. No manual logging, ever." },
    ],
  },
];

export default function Systems() {
  return (
    <section
      id="systems"
      data-testid="systems-section"
      className="relative bg-[#050505] border-t border-white/10 py-28 md:py-40"
    >
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <SectionHeader
          overline="The Build Menu / 04"
          title={<>The 8 Systems<br /><span className="text-[#CCFF00]">We Build.</span></>}
          sub="Every engagement is scoped to your situation. Start with the full stack, or just two or three."
        />

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-0 border border-white/10" data-testid="systems-grid">
          {SYSTEMS.map((sys, i) => (
            <motion.div
              key={sys.n}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: (i % 2) * 0.07 }}
              className="relative p-8 md:p-10 border-t border-l border-white/10 hover:bg-white/[0.02] transition-colors group"
              data-testid={`system-card-${sys.n}`}
            >
              <div className="flex items-baseline justify-between mb-5">
                <span
                  className="font-display font-bold uppercase tracking-tighter text-4xl"
                  style={{ color: sys.color }}
                >
                  {sys.n}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  sys_{sys.n}
                </span>
              </div>

              <h3 className="font-display font-bold uppercase tracking-tighter text-lg md:text-xl text-white leading-tight">
                {sys.name}
              </h3>

              <p className="mt-3 font-body text-[13px] text-zinc-400 leading-relaxed">
                {sys.headline}
              </p>

              <div className="mt-6 pt-5 border-t border-dashed border-white/10 space-y-3">
                {sys.buckets.map((b) => (
                  <div key={b.l} className="flex items-start gap-3">
                    <span
                      className="font-mono text-[10px] font-bold uppercase tracking-wider mt-0.5 shrink-0 w-16"
                      style={{ color: sys.color }}
                    >
                      {b.l}
                    </span>
                    <span className="font-body text-[12px] text-zinc-400 leading-relaxed flex-1">
                      {b.d}
                    </span>
                  </div>
                ))}
              </div>

              <div
                className="pointer-events-none absolute left-0 top-0 h-[2px] w-full opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: sys.color, boxShadow: `0 0 18px ${sys.color}` }}
              />
            </motion.div>
          ))}
        </div>

        <div className="mt-12 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
          <span className="h-px flex-1 bg-white/10" />
          <span>8 systems · 1 runtime · scoped to your stack</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>
      </div>
    </section>
  );
}
